import { Link, useParams, Navigate } from "react-router-dom";
import { Calendar, Clock, ArrowLeft, ArrowRight, ChevronRight, User } from "lucide-react";
import SEOHead from "../components/layout/SEOHead";
import { getArticleBySlug, getRelatedArticles, getReadyArticles } from "../data/articles";

// Image mapping for articles
const imageMap = {
  fullBoomWithPanel: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/mqrx914x_HWL_field_still_03_048s.jpg",
  boomLiftElevated: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/3ack3jic_HWL_field_still_04_060s.jpg",
  scissorLiftPair: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/2hzy6y9m_Highwall%20Scissor%20Lift%20Hanger%20Pair.jpg",
  boomLiftHangers2: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/htgmy3k7_Hanger4.jpg",
  boomLiftMounted: "https://customer-assets.emergentagent.com/job_share-hub-46/artifacts/ujlxoadu_Boom%20Lift%20Hang.jpg",
  liftAndTower: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/9h3z5yos_06_lift-and-tower_square.jpg.jpeg",
  againstBuilding: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/0o94ix6q_07_against-building_square.jpg.jpeg",
  finalPlacement: "https://customer-assets-agu9un31.emergentagent.net/job_share-hub-46/artifacts/cs2x21mp_08_final-placement_square.jpg.jpeg",
};

// Simple markdown-like renderer for article content
const renderContent = (content) => {
  if (!content) return null;
  
  const lines = content.trim().split('\n');
  const elements = [];
  let currentList = [];
  let listType = null;
  
  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-inside space-y-2 text-gray-300 mb-6 pl-4">
          {currentList.map((item, i) => (
            <li key={i}>{item}</li>
          ))}
        </ul>
      );
      currentList = [];
    }
  };
  
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    
    // Headers
    if (trimmed.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={index} className="font-heading text-2xl sm:text-3xl font-bold text-white mt-10 mb-4">
          {trimmed.slice(3)}
        </h2>
      );
    } else if (trimmed.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={index} className="font-heading text-xl sm:text-2xl font-bold text-white mt-8 mb-3">
          {trimmed.slice(4)}
        </h3>
      );
    }
    // List items
    else if (trimmed.startsWith('- **') || trimmed.startsWith('- ')) {
      const content = trimmed.slice(2);
      // Handle bold text
      const boldMatch = content.match(/\*\*(.+?)\*\*(.*)$/);
      if (boldMatch) {
        currentList.push(
          <span key={index}>
            <strong className="text-white">{boldMatch[1]}</strong>
            {boldMatch[2]}
          </span>
        );
      } else {
        currentList.push(content);
      }
    }
    // Numbered list
    else if (/^\d+\.\s/.test(trimmed)) {
      flushList();
      const content = trimmed.replace(/^\d+\.\s/, '');
      const boldMatch = content.match(/\*\*(.+?)\*\*(.*)$/);
      elements.push(
        <div key={index} className="flex gap-3 text-gray-300 mb-2">
          <span className="text-signal-red font-bold">{trimmed.match(/^\d+/)[0]}.</span>
          {boldMatch ? (
            <span><strong className="text-white">{boldMatch[1]}</strong>{boldMatch[2]}</span>
          ) : (
            <span>{content}</span>
          )}
        </div>
      );
    }
    // Paragraphs
    else if (trimmed.length > 0) {
      flushList();
      // Handle inline bold
      const parts = trimmed.split(/(\*\*.+?\*\*)/g);
      const rendered = parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i} className="text-white">{part.slice(2, -2)}</strong>;
        }
        return part;
      });
      elements.push(
        <p key={index} className="text-gray-300 leading-relaxed mb-4">
          {rendered}
        </p>
      );
    }
  });
  
  flushList();
  return elements;
};

const BlogPost = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  
  // Redirect to blog index if article not found or not ready
  if (!article || article.status !== "ready") {
    return <Navigate to="/blog" replace />;
  }
  
  const relatedArticles = getRelatedArticles(slug, 2);
  const allArticles = getReadyArticles();
  const currentIndex = allArticles.findIndex(a => a.slug === slug);
  const prevArticle = currentIndex > 0 ? allArticles[currentIndex - 1] : null;
  const nextArticle = currentIndex < allArticles.length - 1 ? allArticles[currentIndex + 1] : null;
  
  const articleImage = imageMap[article.image];
  
  // Parse date for schema
  const dateMatch = article.date.match(/(\w+)\s(\d+),\s(\d+)/);
  const monthMap = { January: '01', February: '02', March: '03', April: '04', May: '05', June: '06', 
                     July: '07', August: '08', September: '09', October: '10', November: '11', December: '12' };
  const publishedDate = dateMatch 
    ? `${dateMatch[3]}-${monthMap[dateMatch[1]]}-${dateMatch[2].padStart(2, '0')}`
    : new Date().toISOString().split('T')[0];
  
  return (
    <>
      <SEOHead
        title={article.seo?.title || article.title}
        description={article.seo?.metaDescription || article.excerpt}
        keywords={article.seo?.keywords?.join(", ") || "highwall lift, panel installation"}
        canonicalPath={article.seo?.canonicalPath || `/blog/${slug}`}
        ogType="article"
        ogImage={articleImage}
        article={{
          publishedDate,
          author: article.author,
          category: article.category
        }}
      />
      
      <div className="min-h-screen bg-[#0A0A0A] pt-20">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-6">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <Link to="/blog" className="text-gray-400 hover:text-white transition-colors">Blog</Link>
            <ChevronRight className="w-4 h-4 text-gray-600" />
            <span className="text-white truncate max-w-[200px]">{article.title}</span>
          </nav>
        </div>
        
        {/* Article Header */}
        <article className="pb-16" data-testid={`blog-post-${slug}`}>
          <header className="max-w-4xl mx-auto px-6 sm:px-8 mb-10">
            <div className="flex items-center gap-4 text-sm mb-6">
              <span className="bg-signal-red/10 text-signal-red px-3 py-1 font-mono uppercase tracking-wide">
                {article.category}
              </span>
              <span className="flex items-center gap-1 text-gray-500">
                <Calendar className="w-4 h-4" />
                {article.date}
              </span>
              <span className="flex items-center gap-1 text-gray-500">
                <Clock className="w-4 h-4" />
                {article.readTime}
              </span>
            </div>
            
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight mb-6">
              {article.title}
            </h1>
            
            <p className="text-xl text-gray-400 leading-relaxed mb-6">
              {article.excerpt}
            </p>
            
            <div className="flex items-center gap-3 text-gray-400">
              <div className="w-10 h-10 bg-signal-red/20 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-signal-red" />
              </div>
              <span>By <strong className="text-white">{article.author}</strong></span>
            </div>
          </header>
          
          {/* Hero Image */}
          <div className="max-w-5xl mx-auto px-6 sm:px-8 mb-12">
            <div className="aspect-video overflow-hidden border border-white/10">
              <img 
                src={articleImage}
                alt={article.imageAlt || article.title}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          {/* Article Content */}
          <div className="max-w-4xl mx-auto px-6 sm:px-8">
            <div className="prose prose-invert prose-lg max-w-none">
              {renderContent(article.content)}
            </div>
            
            {/* CTA Box */}
            {article.cta && (
              <div className="mt-12 p-8 bg-signal-red/10 border border-signal-red/30" data-testid="article-cta">
                <h3 className="font-heading text-2xl font-bold text-white mb-3">
                  {article.cta.text}
                </h3>
                {article.cta.description && (
                  <p className="text-gray-300 mb-6">{article.cta.description}</p>
                )}
                <Link 
                  to={article.cta.link}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-signal-red text-white font-bold uppercase text-sm tracking-wide hover:bg-signal-red/90 transition-all duration-300"
                >
                  {article.cta.text}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </article>
        
        {/* Navigation Between Articles */}
        <div className="max-w-4xl mx-auto px-6 sm:px-8 py-8 border-t border-white/10">
          <div className="flex justify-between items-center gap-4">
            {prevArticle ? (
              <Link 
                to={`/blog/${prevArticle.slug}`}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
                data-testid="prev-article"
              >
                <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                <span className="hidden sm:inline">Previous</span>
              </Link>
            ) : <div />}
            
            <Link 
              to="/blog"
              className="text-signal-red hover:text-white transition-colors font-medium"
            >
              All Articles
            </Link>
            
            {nextArticle ? (
              <Link 
                to={`/blog/${nextArticle.slug}`}
                className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors group"
                data-testid="next-article"
              >
                <span className="hidden sm:inline">Next</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            ) : <div />}
          </div>
        </div>
        
        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-16 bg-[#141414]">
            <div className="max-w-7xl mx-auto px-6 sm:px-8">
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-8">
                Related Articles
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {relatedArticles.map((related) => (
                  <Link 
                    key={related.id}
                    to={`/blog/${related.slug}`}
                    className="bg-[#1A1A1A] border border-white/10 overflow-hidden group"
                    data-testid={`related-${related.slug}`}
                  >
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={imageMap[related.image]}
                        alt={related.imageAlt || related.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="font-heading text-lg font-bold text-white mb-2 group-hover:text-signal-red transition-colors line-clamp-2">
                        {related.title}
                      </h3>
                      <p className="text-gray-400 text-sm line-clamp-2">
                        {related.excerpt}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </div>
    </>
  );
};

export default BlogPost;
